/* eslint-disable no-nested-ternary */
/* eslint-disable quotes */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { RadioGroup, RadioButton, Button, IconButton } from '@cbrebuild/blocks';
import useCompanyState from '../../../hooks/useCompanyState';
import VoucherContactForm from '../contacts/voucher-contact-form';
import ConfirmationModal from '../../../nucleus/confirmation-modal/confirmation-modal';
import { getAndSetLastPageVisited } from '../voucher-validation';
import CompanyForm from '../CompanyForm';
import formatTelephone from '../../../utils/format-telephone';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import companiesService, { defaultCompanyData } from '../../../services/companies-service';
import { isPartyPocRequired } from '../../../utils/transaction-utils';
import { useReviewRowEditReporter } from '../ReviewTab/ReviewEditTrackingContext';

const QUERY_CONTACT_PARAMS = {
  ordering: '-is_primary',
  page: 1,
  page_size: 25,
};

const CONTACT_MAP = {
  lease: 'tenant',
  sale: 'buyer',
  consulting: 'consulting',
  other: 'other',
};

const isLandlordOrSellerRepDeal = deal => deal.rep_role === 'landlord' || deal.rep_role === 'seller';

const getClientSidePartyKey = deal => (
  (deal.deal_type === 'lease' || deal.deal_type === 'sale') && isLandlordOrSellerRepDeal(deal)
    ? 'counter_party' : 'client'
);

const getCounterSidePartyKey = deal => (isLandlordOrSellerRepDeal(deal) ? 'client' : 'counter_party');

const getPartyKeyForBilledToType = (deal, billedToType) => {
  if (billedToType === 'Tenant' || billedToType === 'Buyer' || billedToType === 'Client') {
    return getClientSidePartyKey(deal);
  }
  if (billedToType === 'Landlord' || billedToType === 'Seller') {
    return getCounterSidePartyKey(deal);
  }
  return 'billed_party';
};

const getPartyForBilledToType = (deal, billedToType) => deal[getPartyKeyForBilledToType(deal, billedToType)];

const getLeaseDetailsSectionUrl = (dealId, section) => (
  `${VOUCHER_ROUTES[1].routes[0].path.replace(':id', dealId)}?section=${section}`
);

const getTypeContactInfo = (data, type) => {
  if (!data) {
    return {};
  }
  const ctTypeData = data.filter(ctData => ctData.contact_type === type);
  if (ctTypeData.length) {
    return ctTypeData[0];
  }
  return [];
};

const getDealNameType = (deal) => {
  const dealNameType = {};
  if (deal.deal_type === 'lease') {
    dealNameType[0] = 'Tenant';
    dealNameType[1] = 'Landlord';
  }
  if (deal.deal_type === 'sale') {
    dealNameType[0] = 'Buyer';
    dealNameType[1] = 'Seller';
  }
  if (deal.deal_type === 'consulting' || deal.deal_type === 'other') {
    dealNameType[1] = 'Client';
  }
  return dealNameType[1];
};

const BillTo = ({
  deal = {},
  voucher = {},
  contactInfo,
  primaryContact,
  updateDeal,
  outsideBrokerCommissions = [],
  addDealContact,
  fetchDealContacts,
  updateDealContact,
  deleteDealContact,
  hideInlineEditIcons,
  toast,
}) => {
  const [displayOtherContactInfo, setDisplayOtherContactInfo] = useState(false);
  const [dealType, setDealType] = useState('');
  const [dealId, setDealId] = useState('');
  const [otherContact, setOtherContact] = useState({});
  const [clientPartyContact, setClientPartyContact] = useState({});
  const [counterPartyContact, setCounterPartyContact] = useState({});
  const [showValidation, setShowValidation] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // Guards against calling createCompany again if this effect re-fires (e.g. React StrictMode's
  // double-invoke in dev) before the deal's billed_to_type has come back from the first call.
  const isCreatingDefaultBilledPartyRef = useRef(false);
  const reportEdit = useReviewRowEditReporter({ rowType: 'bill_to', rowId: deal.id });

  const createOtherBilledParty = () => {
    companiesService.createCompany({
      name: null,
      country: 'United States',
      address: null,
      address2: null,
      city: null,
      territory: null,
      postal: null,
    }).then((createdCompany) => {
      Promise.resolve(updateDeal(deal.id, { billed_party: createdCompany.id, billed_to_type: 'Other' }))
        .then(() => fetchDealContacts({ ...QUERY_CONTACT_PARAMS, deal: deal.id }))
        .then(() => {
          setDisplayOtherContactInfo(true);
        });
    });
  };

  const outside_brokers = outsideBrokerCommissions.filter(commission => commission.vendor_id !== null);
  const clientSideParty = deal[getClientSidePartyKey(deal)];
  const counterSideParty = deal[getCounterSidePartyKey(deal)];
  const companyParty = getPartyForBilledToType(deal, deal.billed_to_type);
  const billedToParty = getPartyKeyForBilledToType(deal, deal.billed_to_type);
  const voucherId = voucher?.id;

  const {
    company, onCompanyBlur, onCompanyChange, onCompanyCreate, searchState, onSuggestionSelect, closeSuggestions,
  } = useCompanyState(
    companyParty || undefined,
    billedToParty,
    update => updateDeal(deal.id, update),
    0,
    true,
    { duplicateOnNameBlur: false },
  );

  // Obtain contact info to be displayed — runs whenever contactInfo updates (no API calls here)
  useEffect(() => {
    const allContacts = [primaryContact, ...contactInfo].filter(Boolean);
    if (deal.billed_to_type !== 'Other') {
      setDisplayOtherContactInfo(false);
      setOtherContact({});
      const clientType = CONTACT_MAP[deal.deal_type];
      const clientContact = getTypeContactInfo(allContacts, clientType);
      if (clientContact.length !== 0) {
        setClientPartyContact(clientContact);
      }
      const counterType = deal.deal_type === 'lease' ? 'landlord' : 'seller';
      const counterContact = getTypeContactInfo(allContacts, counterType);
      if (counterContact.length !== 0) {
        setCounterPartyContact(counterContact);
      }
    } else {
      setDisplayOtherContactInfo(true);
      const otherContactData = getTypeContactInfo(allContacts, 'other');
      if (otherContactData.length !== 0) {
        setOtherContact(otherContactData);
      }
    }
  }, [contactInfo, primaryContact, deal.billed_to_type, deal.deal_type]);

  const billedTypeChange = (event, id) => {
    if (event.target.value.split(' ')[0] === 'Outside') {
      setDealType("Outside Broker");
      setDealId(id);
      if (deal.billed_to_type === 'Other') {
        setShowModal(true);
      } else {
        reportEdit('billed_to_type', deal.billed_to_type, 'Outside Broker');
        updateDeal(deal.id, { billed_party: id, billed_to_type: "Outside Broker" });
      }
    } else {
      setDealType(event.target.value);
      setDealId(id);
      if (deal.billed_to_type === 'Other') {
        setShowModal(true);
      } else if (event.target.value === 'Seller' || event.target.value === 'Buyer' ||
          event.target.value === 'Tenant' || event.target.value === 'Landlord' ||
          event.target.value === 'Client') {
        reportEdit('billed_to_type', deal.billed_to_type, event.target.value);
        updateDeal(deal.id, { billed_party: id, billed_to_type: event.target.value });
      }
      if (event.target.value === 'Other') {
        reportEdit('billed_to_type', deal.billed_to_type, 'Other');
        createOtherBilledParty();
      }
    }
  };

  const closeConfirmationModal = () => {
    setShowModal(false);
  };

  const deleteContact = () => {
    setDisplayOtherContactInfo(false);
    setOtherContact({});
    deleteDealContact(otherContact.id, false);
    reportEdit('billed_to_type', deal.billed_to_type, dealType);
    updateDeal(deal.id, { billed_party: dealId, billed_to_type: dealType });
  };

  // Initialisation — runs once on mount (and if the deal itself changes).
  // fetchDealContacts must NOT be in the dep array: when it resolves it updates
  // contactInfo in Redux which would re-trigger this effect → infinite loop.
  // contactInfo display is handled by the effect above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const currentPageNumber = 2.2;
    const lastPageVisited = getAndSetLastPageVisited(voucherId, currentPageNumber);

    if (lastPageVisited >= currentPageNumber) {
      setShowValidation(true);
    }

    if (deal.billed_to_type === null) {
      const dealNameType = getDealNameType(deal);
      const defaultPartyKey = getPartyKeyForBilledToType(deal, dealNameType);
      const defaultBilledParty = deal[defaultPartyKey];
      if (defaultBilledParty?.id != null) {
        updateDeal(deal.id, { billed_party: defaultBilledParty.id, billed_to_type: dealNameType });
      } else if (!isCreatingDefaultBilledPartyRef.current) {
        // Party record (e.g. Landlord) doesn't exist yet on a brand-new deal — create it first,
        // mirroring Angular's createCounterParty/createClient before defaulting the selection.
        isCreatingDefaultBilledPartyRef.current = true;
        companiesService.createCompany({ ...defaultCompanyData }).then((createdCompany) => {
          updateDeal(deal.id, {
            [defaultPartyKey]: createdCompany.id,
            billed_party: createdCompany.id,
            billed_to_type: dealNameType,
          });
        }).catch((error) => {
          console.log('Error creating default billed party company');
          isCreatingDefaultBilledPartyRef.current = false;
          toast('Unable to set up the billable party for this deal. Please try again or select it manually.');
        });
      }
    }
    if (billedToParty !== 'billed_party' && deal[billedToParty] == null) {
      onCompanyCreate();
    }

    fetchDealContacts({ ...QUERY_CONTACT_PARAMS, deal: deal.id });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id, voucherId]);

  const history = useHistory();
  const hasAllClientInfo = clientPartyContact && clientPartyContact.given_name && clientPartyContact.surname
    && clientPartyContact.email_addresses[0]?.address && clientPartyContact.phone_numbers[0]?.number;
  const hasAllCounterPartyInfo = counterPartyContact && counterPartyContact.given_name && counterPartyContact.surname
    && counterPartyContact.email_addresses[0]?.address && counterPartyContact.phone_numbers[0]?.number;
  const isClientSidePocRequired = isPartyPocRequired(deal, deal.deal_type === 'lease' ? 'tenant' : 'buyer');
  const isCounterSidePocRequired = isPartyPocRequired(deal, deal.deal_type === 'lease' ? 'landlord' : 'seller');

  return (
    <div className="bill-to-tab">
      <legend className="secondary-legend">Who is the Billable Party?</legend>
      <div className="party-header-container">
        <RadioGroup
          name="confidential-options"
          selectedValue={deal.billed_to_type || ""}
          onChange={e => billedTypeChange(e, clientSideParty?.id)}
          className="radioVal"
          orientation="vertical"
        >
          {(deal.deal_type === 'lease' || deal.deal_type === 'sale') ?
            <RadioButton value={(deal.deal_type === 'lease' ? 'Tenant' : 'Buyer')}>
              <div>{(deal.deal_type === 'lease' ? 'Tenant' : 'Buyer')}</div>
            </RadioButton> :
            <RadioButton value={((deal.deal_type === 'other' || deal.deal_type === 'consulting') ? 'Client' : '')}>
              {((deal.deal_type === 'other' || deal.deal_type === 'consulting') ? 'Client' : '')}
            </RadioButton>
          }
          {(clientSideParty &&
            <div className="party-sub-container">
              <div className="info-container">
                <span className="semibold">Company Info:</span>
                <div >{clientSideParty.name && clientSideParty.name}</div>
                <div>{clientSideParty.address && clientSideParty.address} {clientSideParty.address2 && clientSideParty.address2}</div>
                <div>{clientSideParty.city && `${clientSideParty.city},`}
                  {clientSideParty.territory !== null && ` ${clientSideParty.territory}`}
                  {clientSideParty.postal !== null && ` ${clientSideParty.postal}`}
                </div>
              </div>
              <div className="info-container">
                <span className="semibold">Contact Info:</span>
                <div>
                  {clientPartyContact?.given_name && `${clientPartyContact?.given_name} `}
                  {clientPartyContact?.surname && `${clientPartyContact?.surname} `}
                  {clientPartyContact?.job_title && `- ${clientPartyContact?.job_title}`}
                </div>
                <div>{clientPartyContact?.email_addresses && clientPartyContact.email_addresses[0]?.address}</div>
                <div>{clientPartyContact?.phone_numbers && formatTelephone(clientPartyContact.phone_numbers[0]?.number)}</div>
                {isClientSidePocRequired
                  && ((deal.billed_to_type === 'Buyer') || (deal.billed_to_type === 'Client') || (deal.billed_to_type === 'Tenant'))
                  && !hasAllClientInfo && <p className="error-message">Missing required contact information</p>}
              </div>
            </div>
            )}
        </RadioGroup>
        {!hideInlineEditIcons && (
          <IconButton
            iconName="edit"
            onClick={() => {
              switch (deal.deal_type) {
                case 'lease':
                  // New flow: Tenant info lives in the unified Lease Details accordion tab
                  history.push(getLeaseDetailsSectionUrl(deal.id, 'tenantInformation'));
                  break;
                case 'sale':
                  history.push(VOUCHER_ROUTES[1].routes[5].path.replace(':id', deal.id));
                  break;
                case 'consulting':
                case 'other':
                  history.push(VOUCHER_ROUTES[1].routes[8].path.replace(':id', deal.id));
                  break;
                default:
                  break;
              }
            }}
          />
        )}
      </div>
      {(deal.deal_type === 'lease' || deal.deal_type === 'sale') &&
        <div className="party-header-container">
          <RadioGroup
            name="confidential-options"
            selectedValue={deal.billed_to_type || ""}
            onChange={e => billedTypeChange(e, counterSideParty?.id)}
            className="radioVal"
            orientation="vertical"
            checked
          >
            <RadioButton value={deal.deal_type === 'lease' ? 'Landlord' : 'Seller'}>
              {deal.deal_type === 'lease' ? 'Landlord' : 'Seller'}
            </RadioButton>
            {(counterSideParty &&
              <div className="party-sub-container">
                <div className="info-container">
                  <span className="semibold">Company Info:</span>
                  <div>{counterSideParty.name && counterSideParty.name}</div>
                  <div>{counterSideParty.address && counterSideParty.address} {counterSideParty.address2 && counterSideParty.address2}</div>
                  <div>{counterSideParty.city && `${counterSideParty.city},`}
                    {counterSideParty.territory !== null && ` ${counterSideParty.territory}`}
                    {counterSideParty.postal !== null && ` ${counterSideParty.postal}`}
                  </div>
                </div>
                <div className="info-container">
                  <span className="semibold">Contact Info:</span>
                  <div>
                    {counterPartyContact?.given_name && `${counterPartyContact?.given_name} `}
                    {counterPartyContact?.surname && `${counterPartyContact?.surname} `}
                    {counterPartyContact?.job_title && `- ${counterPartyContact?.job_title}`}
                  </div>
                  <div>{counterPartyContact?.email_addresses && counterPartyContact.email_addresses[0]?.address}</div>
                  <div>{counterPartyContact?.phone_numbers && formatTelephone(counterPartyContact.phone_numbers[0]?.number)}</div>
                  {isCounterSidePocRequired
                    && ((deal.billed_to_type === 'Landlord') || (deal.billed_to_type === 'Seller'))
                    && !hasAllCounterPartyInfo && <p className="error-message">Missing required contact information</p>}
                </div>
              </div>
            )}
          </RadioGroup>
          {!hideInlineEditIcons && (
            <IconButton
              iconName="edit"
              onClick={() => {
                switch (deal.deal_type) {
                  case 'lease':
                    // New flow: Landlord info lives in the unified Lease Details accordion tab
                    history.push(getLeaseDetailsSectionUrl(deal.id, 'landlordInformation'));
                    break;
                  case 'sale':
                    history.push(VOUCHER_ROUTES[1].routes[6].path.replace(':id', deal.id));
                    break;
                  default:
                    break;
                }
              }}
            />
          )}
        </div>
      }
      {outside_brokers.length > 0 &&
        <>
          {outside_brokers?.map((broker, index) => (
            <RadioGroup
              name="outside-options"
              selectedValue={`${deal.billed_to_type} ${deal.billed_party?.id}`}
              onChange={e => billedTypeChange(e, broker.vendor.id)}
              className="radioVal"
              orientation="vertical"
              key={broker.id}
            >
              <RadioButton value={`Outside Broker ${broker.vendor.id}`}>
                {outside_brokers.length === 1 ? 'Outside Broker' : `Outside Broker #${index + 1}`}
              </RadioButton>
              <div className="party-sub-container">
                <div className="info-container">
                  <span className="semibold">Company Info:</span>
                  <div>{broker.vendor.name}</div>
                  <div>{broker.vendor.address && broker.vendor.address} {broker.vendor.address2 && broker.vendor.address2}</div>
                  <div>{broker.vendor.city && `${broker.vendor.city},`}
                    {broker.vendor.territory !== null && ` ${broker.vendor.territory}`}
                    {broker.vendor.postal !== null && ` ${broker.vendor.postal}`}
                  </div>
                </div>
                <div className="info-container">
                  <span className="semibold">Contact Info:</span>
                  <div>
                    {broker.outside_broker_name}
                    {broker.outside_broker_title && ` - ${broker.outside_broker_title}`}
                  </div>
                  <div>{broker.outside_broker_email}</div>
                  <div>{formatTelephone(broker.outside_broker_phone_number)}</div>
                </div>
              </div>
            </RadioGroup>

          ))}
        </>
      }
      <RadioGroup
        name="confidential-options"
        selectedValue={deal.billed_to_type || ""}
        onChange={billedTypeChange}
        className="radioVal"
        orientation="vertical"
      >
        <RadioButton value="Other">
          Other
        </RadioButton>
      </RadioGroup>
      {displayOtherContactInfo &&
        <React.Fragment>
          <h2>Bill To</h2>
          <legend>Information</legend>
          <CompanyForm
            infoType="BillTo"
            partyType="other"
            {...company}
            onChange={onCompanyChange}
            onBlur={onCompanyBlur}
            showValidation={showValidation}
            suggestions={searchState.suggestions}
            showSuggestions={searchState.showSuggestions}
            loadingSearch={searchState.loading}
            onSuggestionSelect={onSuggestionSelect}
            closeSuggestions={closeSuggestions}
          />
          <VoucherContactForm
            dealId={deal.id}
            contactInfo={contactInfo}
            updateDeal={updateDeal}
            addDealContact={addDealContact}
            fetchDealContacts={fetchDealContacts}
            updateDealContact={updateDealContact}
            deleteDealContact={deleteDealContact}
            contactType="other"
            showValidation={showValidation}
            hasClrBtn={false}
          />
        </React.Fragment>
      }
      {showModal &&
        <ConfirmationModal
          className="deal-file-delete-confirmation tenant-modal voucher-modal"
          onCloseModal={closeConfirmationModal}
          showModal={showModal}
        >
          <div className="header-modal">
            <h1 className="header-content conf-header">Confirmation</h1>
            <IconButton
              iconName="close-circle"
              className="icon-circle"
              variant="basic"
              onClick={closeConfirmationModal}
            />
          </div>
          <div className="conf-text">
            <p>This will delete the Other billed party contact. Are you sure?</p>
          </div>
          <footer className="actions">
            <Button
              className="cancel-btn"
              variant="secondary"
              onClick={closeConfirmationModal}
            >
              Cancel
            </Button>
            <Button
              className="upload-btn ok-btn"
              onClick={() => {
              setShowModal(false);
              deleteContact();
          }}>
              Ok
            </Button>
          </footer>
        </ConfirmationModal>
      }
    </div>
  );
};

BillTo.propTypes = {
  voucher: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  deal: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  updateDeal: PropTypes.func.isRequired,
  addDealContact: PropTypes.func.isRequired,
  outsideBrokerCommissions: PropTypes.arrayOf(PropTypes.shape({})),
  updateDealContact: PropTypes.func.isRequired,
  deleteDealContact: PropTypes.func.isRequired,
  fetchDealContacts: PropTypes.func.isRequired,
  contactInfo: PropTypes.arrayOf(PropTypes.shape({})),
  primaryContact: PropTypes.shape({}),
  hideInlineEditIcons: PropTypes.bool,
  toast: PropTypes.func.isRequired,
};

BillTo.defaultProps = {
  contactInfo: [],
  outsideBrokerCommissions: [],
  primaryContact: null,
  hideInlineEditIcons: false,
};

export default BillTo;
